"use client";

import React, { useCallback, useMemo, useState } from "react";
// @ts-ignore
import { z as zod } from "zod";
import isEmpty from "lodash/isEmpty";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useRouter } from "next/navigation";

import { Iconify } from "@onair/minimal-ui/iconify";

import LoadingButton from "@mui/lab/LoadingButton";
import {
	Box,
	Button,
	Container,
	formLabelClasses,
	inputLabelClasses,
	Popover,
	Stack,
	Typography,
} from "@mui/material";

import { toast } from "@onair/minimal-ui/snackbar";
import { Form } from "@onair/minimal-ui/hook-form";

import { useWarnIfUnsavedChanges } from "@onair/hooks/use-before-unload";

import WarningEventDraftDialog from "./dialogs/warning-event-draft-dialog";
import WarningEventValidationDialog from "./dialogs/warning-event-validation-dialog";
import { Stepper, StepperItem } from "./stepper";
import StepType from "./step-type";
import { StepInfoSchema } from "./step-info";
import { StepResourceSchema } from "./step-resource";
import {
	EventStatusV2,
	EventTypeV2,
	EventV2Response,
	LocationType,
	SessionPriceType,
} from "@onair/repositories";
import StepNavigator from "./step-navigator";
import StepFormWizard from "./step-form-wizard";
import { StepTimelineSchema } from "./step-timeline";
import { StepPricingSchema } from "./step-pricing";
import { CommonBarSvg } from "@onair/ui/svg/common-bar";
import { MiniTicketSvg } from "@onair/ui/svg/mini-ticket";
import { MiniFileSvg } from "@onair/ui/svg/mini-file";
import { MiniCalendarSvg } from "@onair/ui/svg/mini-calendar";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	createEventV2,
	handlePrepareEventFormData,
	updateEventV2,
} from "./helper";
import omit from "lodash/omit";
import { StepTimelineAndPricingSchema } from "./validations";
import { GET_EVENT_BY_ID_V2, GET_MINE_EVENTS_V2 } from "@onair/operations";
import DialogSuccess from "./dialogs/dialog-success";

interface PropTypes {
	initData?: EventV2Response;
	onCancel?: () => void;
	onSuccess?: () => void;
}

const SingleEventSchema = zod.object({
	type: zod.literal(EventTypeV2.SINGLE),
	locationType: zod.nativeEnum(LocationType, {
		required_error: "Vui lòng chọn loại địa điểm",
	}),
	stepTimelineAndPricing: StepTimelineAndPricingSchema,
});

const EventSessionSchema = zod.object({
	type: zod.literal(EventTypeV2.SESSION),
	locationType: zod.nativeEnum(LocationType, {
		required_error: "Vui lòng chọn loại địa điểm",
	}),
	stepTimeline: StepTimelineSchema,
	stepPricing: StepPricingSchema,
});

const CondSchema = zod.discriminatedUnion("type", [
	SingleEventSchema,
	EventSessionSchema,
]);

const DefaultSchema = zod.object({
	type: zod.nativeEnum(EventTypeV2, {
		required_error: "Vui lòng chọn loại sự kiện",
	}),
	locationType: zod.nativeEnum(LocationType, {
		required_error: "Vui lòng chọn loại địa điểm",
	}),
	stepInfo: StepInfoSchema,
	stepResource: StepResourceSchema,
	//
	_id: zod.string().optional(),
	hasBeenPaid: zod.boolean().optional(),
	productId: zod.string().optional(),
});

export const WizardSchema = zod.intersection(DefaultSchema, CondSchema);

export type WizardSchemaType = zod.infer<typeof WizardSchema> & {
	stepTimelineAndPricing: zod.infer<typeof StepTimelineAndPricingSchema>;
	stepTimeline: zod.infer<typeof StepTimelineSchema>;
	stepPricing: zod.infer<typeof StepPricingSchema>;
};

const EventFormStepper: React.FC<PropTypes> = (props) => {
	const { initData, onCancel, onSuccess } = props;

	const router = useRouter();
	const [activeStep, setActiveStep] = useState(0);
	const [canExit, setCanExit] = useState(false);
	const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
	const [isDrafting, setIsDrafting] = useState(false);
	const [shouldOpenWarningModal, setShouldOpenWarningModal] = useState(false);
	const [shouldOpenWarningDraftModal, setShouldOpenWarningDraftModal] =
		useState(false);
	const [started, setStarted] = useState(Boolean(initData));
	const queryClient = useQueryClient();

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

	const {
		isOpen,
		openDialog: openDialogSuccess,
		closeDialog: closeDialogSuccess,
	} = DialogSuccess.useDialog();

	const [dialogMessage, setDialogMessage] = useState<React.ReactNode>();
	const { mutateAsync: mutateEvent, isPending: isPendingUpsert } = useMutation({
		mutationFn: (
			data: WizardSchemaType & { isDraft?: boolean; isPreview?: boolean },
		) => {
			const { isDraft = false, isPreview = false } = data;

			if (isDraft) setIsDrafting(true);
			if (isPreview) setIsGeneratingPreview(true);

			if (initData?.id && !isPreview) {
				return updateEventV2(
					initData.id,
					omit(data, ["isDraft", "isPreview"]),
					{
						isDraft,
					},
				).finally(() => {
					setIsDrafting(false);
					setIsGeneratingPreview(false);
					queryClient.invalidateQueries({ queryKey: [GET_EVENT_BY_ID_V2] });
					queryClient.invalidateQueries({ queryKey: [GET_MINE_EVENTS_V2] });
				});
			}
			return createEventV2(omit(data, ["isDraft", "isPreview"]), {
				isDraft,
				isPreview,
			}).finally(() => {
				setIsDrafting(false);
				setIsGeneratingPreview(false);
				queryClient.invalidateQueries({ queryKey: [GET_EVENT_BY_ID_V2] });
				queryClient.invalidateQueries({ queryKey: [GET_MINE_EVENTS_V2] });
			});
		},
	});

	const methods = useForm<WizardSchemaType>({
		mode: "all",
		resolver: zodResolver(WizardSchema),
		defaultValues: useMemo(() => {
			return handlePrepareEventFormData(initData || null);
		}, [initData]),
	});

	const {
		trigger,
		setValue,
		getValues,
		formState: { isSubmitting, errors },
		control,
		handleSubmit,
	} = methods;

	const shouldDisableSubmit = useMemo(() => {
		return isDrafting || isGeneratingPreview;
	}, [isDrafting, isGeneratingPreview]);

	const stepType = useWatch({
		name: "type",
		control,
	});

	const hasBeenPaid = useMemo(() => {
		const stepTimelineAndPricing = getValues("stepTimelineAndPricing");
		const stepPricing = getValues("stepPricing");

		if (stepType === EventTypeV2.SESSION) {
			const locationType = getValues("locationType");
			if (
				locationType === LocationType.ONLINE &&
				stepPricing?.priceType === SessionPriceType.ALL
			) {
				return stepPricing?.ticketTiers?.[0]?.hasBeenPaid;
			}

			return stepPricing?.sessions?.length
				? stepPricing?.sessions?.some((session) =>
						session.ticketTiers?.length
							? session.ticketTiers.some(
									(ticketTier) => ticketTier?.hasBeenPaid,
								)
							: false,
					)
				: false;
		}

		return stepTimelineAndPricing?.ticketTiers?.[0]?.hasBeenPaid;
	}, [stepType]);

	const steps: StepperItem[] = useMemo(() => {
		if (stepType === EventTypeV2.SESSION) {
			return EVENT_SESSION_STEPS;
		}

		return SINGLE_EVENT_STEPS;
	}, [stepType]);

	useWarnIfUnsavedChanges(
		!isEmpty(canExit || methods.formState?.dirtyFields) &&
			!isSubmitting &&
			!isPendingUpsert,
	);

	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const handleStart = () => {
		setStarted(true);
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const handleBack = useCallback(() => {
		if (activeStep === 0) {
			setStarted(false);
		} else {
			setActiveStep((prevActiveStep) => prevActiveStep - 1);
		}
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, [activeStep]);

	const onSubmit = handleSubmit(
		async (data: WizardSchemaType) => {
			try {
				const event = await mutateEvent(data);

				if (event) {
					if (onSuccess) {
						onSuccess();
						return;
					}
					window.location.href = `/event/${event.slug}/success`;
					return;
				}

				throw new Error("Tạo sự kiện thất bại");
			} catch (error) {
				toast.error((error as Error).message);

				console.error(error);
			}
		},
		(errors) => {
			console.log("[DEBUG] Form Submit Error:", errors);
		},
	);

	const handleOpenWarningDraftModal = () => {
		setShouldOpenWarningDraftModal(true);
	};

	const handleDraft = useCallback(
		async (params = { isPreview: false }) => {
			const { isPreview } = params;
			const values = getValues();

			try {
				const draftedEvent = await mutateEvent({
					...values,
					isDraft: true,
					isPreview,
				});

				if (draftedEvent) {
					if (!isPreview) {
						toast.success("Lưu nháp sự kiện thành công.");

						if (onSuccess) {
							onSuccess();
							return;
						}
						router.push("/events?state=draft");
						return;
					}

					window.open(`/event/${draftedEvent.slug}`, "_blank")?.focus();
					return;
				}
			} catch (e) {
				toast.error(`${isPreview ? "Tạo xem trước" : "Lưu nháp"} thất bại`);
			}
		},
		[getValues],
	);

	const handleFakeDraft = () => {
		setDialogMessage(
			<>
				<Typography className="font-semibold text-3xl mb-2">
					Hoàn tất
				</Typography>
				<Typography component="p" className="text-gray-500 mb-8">
					Chúc mừng bạn đã tạo bản nháp thành công.
				</Typography>
			</>,
		);
		openDialogSuccess();
	};
	const handleCreate = useCallback(async () => {
		const isValid = await trigger();

		if (!isValid) {
			setShouldOpenWarningModal(true);
			return;
		}

		setDialogMessage(
			<>
				<Typography className="font-semibold text-3xl mb-2">
					Hoàn tất
				</Typography>
				<Typography component="p" className="text-gray-500 mb-8">
					Chúc mừng bạn đã sự kiện thành công.
				</Typography>
			</>,
		);
		openDialogSuccess();
		// await onSubmit();
	}, [trigger, onSubmit, setValue]);

	const handleStepClick = (step: number) => {
		setActiveStep(step);
	};

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseWarningModal = () => {
		setShouldOpenWarningModal(false);
	};

	const handleCloseWarningDraftModal = () => {
		setShouldOpenWarningDraftModal(false);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleCancel = () => {
		if (onCancel) {
			onCancel();
			return;
		}

		if (window.history.length > 1) {
			router.back();
			return;
		}

		router.push("/events");
	};

	const open = Boolean(anchorEl);

	const isCompletedStep = activeStep === steps.length;
	const isLastStep = activeStep === steps.length - 1;

	const stepNavigator = React.useMemo(() => {
		return (
			<StepNavigator
				started={started}
				isCompletedStep={isCompletedStep}
				isLastStep={isLastStep}
				disabledBack={
					initData?.status === EventStatusV2.PUBLISHED && activeStep === 0
				}
				onNext={handleNext}
				onBack={handleBack}
				mt={{
					xs: 3,
					md: 0,
				}}
			/>
		);
	}, [
		started,
		activeStep,
		initData?.status,
		isCompletedStep,
		isLastStep,
		handleNext,
		handleBack,
	]);

	console.log("[DEBUG] Form Error:", errors);

	return (
		<Box
			sx={{
				backgroundColor: {
					xs: "white",
					md: "#F4F6F8",
				},
				height: "100%",
				flex: 1,
				pb: 4,
			}}
		>
			{started && (
				<Box
					sx={{
						backgroundColor: "#fff",
						mb: {
							xs: 0,
							md: 4,
						},
					}}
				>
					<Container
						sx={{
							px: {
								xs: 2,
								md: 3,
							},
						}}
						maxWidth="lg"
					>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							spacing={3}
						>
							<Stack
								pt={2}
								sx={{
									pb: {
										xs: 3,
										md: 0,
									},
								}}
							>
								<Typography fontWeight={700} variant="h6">
									{initData?.id ? "Chỉnh sửa sự kiện" : "Tạo sự kiện"}
								</Typography>
								<Box flex={1} display={{ xs: "none", md: "block" }}>
									<Stepper
										onStepClick={handleStepClick}
										steps={steps}
										activeStep={activeStep}
										methods={methods}
									/>
								</Box>
							</Stack>
							<Stack
								direction="row"
								sx={{
									height: {
										xs: 36,
										md: 48,
									},
									gap: {
										xs: 1,
										md: 1.5,
									},
								}}
							>
								<Button
									variant="outlined"
									sx={{
										width: 36,
										minWidth: "unset",
										p: 0,
										display: {
											xs: "block",
											md: "none",
										},
									}}
									onClick={handleClick}
								>
									<Iconify width={20} icon="eva:arrow-ios-downward-fill" />
								</Button>
								<Popover open={open} anchorEl={anchorEl} onClose={handleClose}>
									<Stack minWidth={200}>
										<LoadingButton
											loading={isDrafting}
											disabled={isSubmitting || hasBeenPaid}
											onClick={handleOpenWarningDraftModal}
											sx={{ justifyContent: "start" }}
											fullWidth
										>
											<Iconify color="text.secondary" icon="solar:inbox-bold" />
											<Typography
												align="left"
												variant="body2"
												sx={{
													pl: 1,
												}}
											>
												Lưu nháp
											</Typography>
										</LoadingButton>
										<LoadingButton
											disabled={isSubmitting}
											loading={isGeneratingPreview}
											onClick={() => handleDraft({ isPreview: true })}
											sx={{ justifyContent: "start" }}
											fullWidth
										>
											<Iconify color="text.secondary" icon="solar:eye-bold" />
											<Typography
												align="left"
												variant="body2"
												sx={{
													pl: 1,
												}}
											>
												Xem trước
											</Typography>
										</LoadingButton>
										<Button
											disabled={isSubmitting}
											onClick={handleCancel}
											sx={{ justifyContent: "start" }}
											fullWidth
										>
											<Iconify
												color="text.secondary"
												icon="mingcute:close-line"
											/>
											<Typography
												align="left"
												variant="body2"
												sx={{
													pl: 1,
												}}
											>
												Huỷ
											</Typography>
										</Button>
									</Stack>
								</Popover>
								<Button
									aria-label="close"
									variant="outlined"
									disabled={isSubmitting}
									onClick={handleCancel}
									sx={{
										width: 48,
										minWidth: "unset",
										display: {
											xs: "none",
											md: "block",
										},
									}}
								>
									<Iconify width={20} icon="mingcute:close-line" />
								</Button>
								{/* <LoadingButton
									disabled={isSubmitting}
									loading={isGeneratingPreview}
									// onClick={() => handleDraft({ isPreview: true })}
									variant="outlined"
									sx={{
										width: 48,
										minWidth: "unset",
										display: {
											xs: "none",
											md: "block",
										},
									}}
								>
									<Iconify width={20} icon="solar:eye-bold" />
								</LoadingButton> */}
								<LoadingButton
									loading={isDrafting}
									onClick={handleOpenWarningDraftModal}
									disabled={isSubmitting || hasBeenPaid}
									variant="outlined"
									sx={{
										px: 2,
										display: {
											xs: "none",
											md: "block",
										},
									}}
								>
									Lưu nháp
								</LoadingButton>
								<LoadingButton
									disabled={shouldDisableSubmit}
									loading={isSubmitting}
									onClick={handleCreate}
									variant="contained"
									sx={{ px: 2 }}
								>
									{initData?.status === EventStatusV2.PUBLISHED
										? "Chỉnh sửa"
										: "Đăng tải"}
								</LoadingButton>
							</Stack>
						</Stack>
						<Box display={{ xs: "block", md: "none" }}>
							<Stepper
								onStepClick={handleStepClick}
								steps={steps}
								activeStep={activeStep}
								methods={methods}
								isMobile
							/>
						</Box>
					</Container>
				</Box>
			)}
			<Container
				sx={{
					px: {
						xs: 2,
						md: 3,
					},
					py: {
						xs: 3,
						md: 0,
					},
					[`& .${formLabelClasses.asterisk}`]: {
						color: "error.main",
					},
					[`& .${inputLabelClasses.asterisk}`]: {
						color: "error.main",
					},
				}}
				maxWidth="lg"
			>
				<Form methods={methods}>
					{!started ? (
						<Stack
							mx="auto"
							sx={{
								py: {
									xs: 0,
									md: 11,
								},
							}}
							width="100%"
						>
							<Box display="inline" mx="auto">
								<StepType
									isEdit={Boolean(initData)}
									methods={methods}
									onStart={handleStart}
								/>
							</Box>
						</Stack>
					) : (
						<StepFormWizard
							activeStep={activeStep}
							stepType={stepType}
							methods={methods}
							stepNavigator={stepNavigator}
						/>
					)}
				</Form>
				<WarningEventValidationDialog
					isDrafting={isDrafting}
					disabledDraft={hasBeenPaid}
					open={shouldOpenWarningModal}
					onClose={handleCloseWarningModal}
					// onDraft={() => handleDraft()}
					onDraft={handleFakeDraft}
				/>
				<WarningEventDraftDialog
					isDrafting={isDrafting}
					open={shouldOpenWarningDraftModal}
					onClose={handleCloseWarningDraftModal}
					// onDraft={() => handleDraft()}
					onDraft={handleFakeDraft}
				/>
				<DialogSuccess
					content={dialogMessage}
					isOpen={isOpen}
					// onClose={closeDialogSuccess}
				/>
			</Container>
		</Box>
	);
};

const SINGLE_EVENT_STEPS = [
	{
		label: "Thông tin chung",
		activeIcon: <CommonBarSvg color="active" />,
		defaultIcon: <CommonBarSvg color="default" />,
		errorIcon: <CommonBarSvg color="error" />,
	},
	{
		label: "Thời gian & Giá vé",
		activeIcon: <MiniTicketSvg color="active" />,
		defaultIcon: <MiniTicketSvg color="default" />,
		errorIcon: <MiniTicketSvg color="error" />,
	},
	{
		label: "Tài nguyên",
		activeIcon: <MiniFileSvg color="active" />,
		defaultIcon: <MiniFileSvg color="default" />,
		errorIcon: <MiniFileSvg color="error" />,
		optional: true,
	},
];

const EVENT_SESSION_STEPS = [
	{
		label: "Thông tin chung",
		activeIcon: <CommonBarSvg color="active" />,
		defaultIcon: <CommonBarSvg color="default" />,
		errorIcon: <CommonBarSvg color="error" />,
	},
	{
		label: "Thời gian",
		activeIcon: <MiniCalendarSvg color="active" />,
		defaultIcon: <MiniCalendarSvg color="default" />,
		errorIcon: <MiniCalendarSvg color="error" />,
	},
	{
		label: "Giá vé",
		activeIcon: <MiniTicketSvg color="active" />,
		defaultIcon: <MiniTicketSvg color="default" />,
		errorIcon: <MiniTicketSvg color="error" />,
	},
	{
		label: "Tài nguyên",
		defaultIcon: <MiniFileSvg color="default" />,
		activeIcon: <MiniFileSvg color="active" />,
		errorIcon: <MiniFileSvg color="error" />,
		optional: true,
	},
];

export default EventFormStepper;
